'use client'

/**
 * SearchableMultiSelect
 *
 * Multi-select variant of `SearchableSelect`. Keeps the popover open on
 * selection so the user can pick several items. Each row shows a checkbox
 * indicator. The trigger displays a count badge when items are selected.
 * A "Clear all" row is shown at the top whenever the selection is non-empty.
 *
 * ## Why this lives in its own file (#13473)
 *
 * It was the second half of `searchable-select.tsx` until grouped mode pushed
 * that file past the 1000-line `check:file-size-ratchet` cap, and the two
 * components are the natural seam: they share styling and the popover
 * geometry type, nothing else. Moving the SMALLER, unchanged half keeps the
 * diff of the half that DID change readable.
 *
 * `searchable-select.tsx` re-exports both names, so every existing import
 * site — nine callers and four test suites — is untouched. That module stays
 * the public entry point for the pair; import from here only in new code that
 * wants the multi-select alone.
 *
 * ## Grouping (`getGroup`) is deliberately NOT implemented here
 *
 * #13473 added optional group headings to `SearchableSelect` only. This
 * component has its own index space — the shared `optionRowId` helper under
 * its own `listboxId` (#13504) — and no caller needs headings; adding them
 * would duplicate the run logic against a second index space for a
 * hypothetical consumer. Do it when one exists, and share `sortByGroup` /
 * `toGroupRuns` from `searchable-select.tsx` when you do.
 *
 * ## Where `role="listbox"` lives (#13479)
 *
 * On the scrolling option list, together with `aria-multiselectable` and the
 * `listboxId` the trigger's `aria-controls` resolves to — NOT on the portal
 * root, which also owns the search field and so would make the listbox the
 * owner of a `textbox` (`aria-required-children`, critical) as well as
 * leaving it unnamed (`aria-input-field-name`, serious). The name comes from
 * `./searchable-select-listbox-name`, which explains why it is the trigger's
 * label as a string rather than an `aria-labelledby` pointing at it. The
 * portal root keeps {@link SEARCHABLE_SELECT_POPOVER_ATTR} so a test can
 * still address the popover as a whole.
 *
 * `aria-activedescendant` deliberately did NOT move with the role: it is on
 * the SEARCH FIELD, which is the element that holds DOM focus while the
 * popover is open, and ARIA honours the attribute only on the focused
 * element. The field is therefore the `role="combobox"` here, carrying
 * `aria-expanded` and `aria-controls={listboxId}`.
 * `searchable-select.tsx`'s header carries the full rationale (item 4); this
 * is the same change applied to the same markup.
 */

import { SearchInput } from '@sai/ui/components'
import {
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown, X } from '@/components/icons'
import { useClickOutside } from '@/hooks/use-click-outside'
import { cn } from '@/lib/utils'
import {
  listboxAccessibleName,
  SEARCHABLE_SELECT_POPOVER_ATTR,
} from './searchable-select-listbox-name'
import { type MenuPos, measureMenuPos } from './searchable-select-menu-pos'
import {
  clearRowId,
  findRowIn,
  focusedRowId,
  optionRowId,
} from './searchable-select-row-ids'
import { SEARCH_FIELD_CLASSES } from './searchable-select-search-field'
import {
  TriggerNameText,
  TriggerValue,
  triggerLabelledBy,
  triggerNameTextId,
  triggerValueId,
} from './searchable-select-trigger-name'

export interface SearchableMultiSelectProps<T> {
  /** Selected item ids. */
  value: string[]
  /** Called with the new set of selected item ids. */
  onChange: (ids: string[]) => void
  /** Items available for selection. */
  items: T[]
  /** Extract the stable id from an item. */
  getId: (item: T) => string
  /** Text matched against the search query (case-insensitive). */
  getSearchText: (item: T) => string
  /** Row content for an item in the option list. */
  renderOption: (item: T) => ReactNode
  /** Trigger text shown when nothing is selected. */
  placeholder?: string
  /** Placeholder for the search input. */
  searchPlaceholder?: string
  /** Label for the clear-all option shown when items are selected. */
  clearLabel?: string
  /** Message shown when no items match the query. */
  emptyLabel?: string
  disabled?: boolean
  /** id applied to the trigger button (for label association). */
  id?: string
  /**
   * Names the trigger, followed by its visible value, when there is no
   * visible label to point `ariaLabelledBy` at; also names the option list.
   * See `./searchable-select-trigger-name`.
   */
  ariaLabel?: string
  /**
   * id of the visible label. The trigger is named by it plus its visible
   * value (#16638); takes precedence over `ariaLabel` for the trigger.
   */
  ariaLabelledBy?: string
  /** id of the help text that describes the trigger button. */
  ariaDescribedBy?: string
  className?: string
  /**
   * Class applied to a checked option row.
   * Defaults to `bg-status-blue-bg-light`.
   */
  selectedClassName?: string
}

/** See this file's header for what this control is and why it lives here. */
export function SearchableMultiSelect<T>({
  value,
  onChange,
  items,
  getId,
  getSearchText,
  renderOption,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  clearLabel = 'Clear all',
  emptyLabel = 'No matches found',
  disabled = false,
  id,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  className,
  selectedClassName = 'bg-status-blue-bg-light',
}: SearchableMultiSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false)
  // See SearchableSelect above: `aria-controls` needs a stable listbox id.
  const fallbackId = useId()
  const baseId = id ?? fallbackId
  const listboxId = `${baseId}-listbox`
  // The trigger's name: a label, then what it shows (#16638).
  const valueId = triggerValueId(baseId)
  const nameText = ariaLabelledBy ? undefined : ariaLabel?.trim()
  const labelId = ariaLabelledBy ?? (nameText && triggerNameTextId(baseId))
  const [query, setQuery] = useState('')
  /**
   * Keyboard-focused row index. -1 = the "clear all" row (when selection is
   * non-empty), 0..N-1 = filtered items. null = no arrow-key focus active.
   */
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const listboxRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  // Fixed-position coords for the portaled popover (see useLayoutEffect below).
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null)

  // Close on click outside the trigger OR the (portaled) popover.
  useClickOutside([containerRef, dropdownRef], () => setIsOpen(false), isOpen)

  // Portal the popover to <body> so an overflow ancestor can't clip it; keep
  // its fixed coords in sync with the trigger while open (#10298).
  useLayoutEffect(() => {
    if (!isOpen) return
    const update = () => {
      const el = containerRef.current
      if (!el) return
      setMenuPos(measureMenuPos(el))
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [isOpen])

  // Auto-focus the search field when the popover opens.
  useEffect(() => {
    if (isOpen) {
      searchRef.current?.focus()
      setFocusedIndex(null)
    }
  }, [isOpen])

  // Reset keyboard focus when query changes.
  useEffect(() => {
    setFocusedIndex(null)
  }, [query])

  const q = query.toLowerCase()
  const filtered = items.filter(item =>
    getSearchText(item).toLowerCase().includes(q),
  )

  const hasClearRow = value.length > 0
  const minIndex = hasClearRow ? -1 : 0
  const maxIndex = filtered.length - 1

  const open = () => {
    if (disabled) return
    setQuery('')
    // Position synchronously so the portal mounts the same render and the
    // search input auto-focuses (#10298).
    const el = containerRef.current
    if (el) setMenuPos(measureMenuPos(el))
    setIsOpen(true)
  }

  const toggle = (itemId: string) => {
    if (value.includes(itemId)) {
      onChange(value.filter(id => id !== itemId))
    } else {
      onChange([...value, itemId])
    }
    // Popover stays open — multi-select keeps picking.
  }

  const clearAll = () => {
    onChange([])
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      // See SearchableSelect above: without stopPropagation, useModalEscape's
      // document listener also closes the surrounding Modal (#12636).
      e.stopPropagation()
      setIsOpen(false)
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (filtered.length === 0 && !hasClearRow) return
      setFocusedIndex(prev => {
        if (prev === null) return hasClearRow ? -1 : 0
        return Math.min(prev + 1, maxIndex)
      })
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (filtered.length === 0 && !hasClearRow) return
      setFocusedIndex(prev => {
        if (prev === null) return maxIndex
        return Math.max(prev - 1, minIndex)
      })
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      if (focusedIndex === null) return
      if (focusedIndex === -1) {
        clearAll()
      } else {
        const item = filtered[focusedIndex]
        if (item) toggle(getId(item))
      }
    }
  }

  // Scroll focused option into view when arrow-key focus changes.
  useEffect(() => {
    if (!listboxRef.current) return
    const focusedElemId = focusedRowId(listboxId, focusedIndex)
    if (!focusedElemId) return
    // Resolved by `getElementById` plus a containment check rather than by a
    // selector, for the reason `./searchable-select-row-ids` gives: the prefix
    // carries the caller's unvetted `id` prop, and a `.`, `"` or `\` in it
    // breaks one selector form or another.
    const el = findRowIn(listboxRef.current, focusedElemId)
    el?.scrollIntoView({ block: 'nearest' })
  }, [focusedIndex, listboxId])

  // Instance-scoped (#13504); same derivation as the rows below.
  const activedescendant = focusedRowId(listboxId, focusedIndex)

  // `role="listbox"` requires an accessible name (#13479); see the helper.
  const listboxLabel = listboxAccessibleName(ariaLabel, placeholder)

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {nameText ? (
        <TriggerNameText id={triggerNameTextId(baseId)} text={nameText} />
      ) : null}
      <button
        type="button"
        id={id}
        aria-labelledby={triggerLabelledBy(labelId, valueId)}
        aria-describedby={ariaDescribedBy}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        disabled={disabled}
        onClick={() => (isOpen ? setIsOpen(false) : open())}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 text-left',
          'border border-stroke-light rounded-lg bg-surface-elevated',
          'focus:outline-none focus:ring-2 focus:ring-stroke-selected',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
      >
        <TriggerValue id={valueId} placeholder={placeholder}>
          {value.length > 0 ? (
            <span className="flex-1 truncate text-text-primary">
              {value.length === 1 ? '1 selected' : `${value.length} selected`}
            </span>
          ) : null}
        </TriggerValue>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-text-secondary transition-transform flex-shrink-0',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {isOpen &&
        !disabled &&
        menuPos &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={dropdownRef}
            // The popover SHELL — no role of its own, because it owns the
            // search field, which a listbox may not own (#13479). The marker
            // is the addressable hook for a test that needs the whole
            // popover rather than just the option list.
            {...{ [SEARCHABLE_SELECT_POPOVER_ATTR]: '' }}
            tabIndex={-1}
            // `z-menu`: above any modal or drawer (#16990); fixed coords keep an overflow
            // ancestor from clipping it (#10298); flips + clamps (#16177).
            style={{
              position: 'fixed',
              top: menuPos.top,
              bottom: menuPos.bottom,
              left: menuPos.left,
              width: menuPos.width,
              maxHeight: menuPos.maxHeight,
            }}
            data-placement={menuPos.placement}
            className="z-menu flex flex-col overflow-hidden rounded-lg border border-stroke-regular bg-surface-elevated shadow-md"
          >
            <div className="p-2 border-b border-stroke-regular">
              <SearchInput
                ref={searchRef}
                ariaLabel={searchPlaceholder}
                // The COMBOBOX: this is the element that holds DOM focus
                // while the popover is open, and `aria-activedescendant` is
                // honoured only on the focused element. See
                // `searchable-select.tsx`'s item 4 for the full reason.
                role="combobox"
                aria-expanded={isOpen}
                aria-controls={listboxId}
                aria-activedescendant={activedescendant}
                hideClear
                size="sm"
                value={query}
                placeholder={searchPlaceholder}
                onChange={setQuery}
                onKeyDown={handleSearchKeyDown}
                className={SEARCH_FIELD_CLASSES}
              />
            </div>
            <div
              ref={listboxRef}
              id={listboxId}
              role="listbox"
              tabIndex={-1}
              aria-multiselectable="true"
              aria-label={listboxLabel}
              // No `aria-activedescendant` here on purpose: it lives on the
              // search field, which is the element that actually has focus.
              className="max-h-60 min-h-0 overflow-y-auto p-1"
            >
              {hasClearRow && (
                <button
                  id={clearRowId(listboxId)}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={clearAll}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-md text-text-secondary',
                    focusedIndex === -1
                      ? 'bg-fill-10 outline-none ring-2 ring-stroke-selected'
                      : 'hover:bg-fill-10',
                  )}
                >
                  <X className="w-3.5 h-3.5 flex-shrink-0" />
                  {clearLabel}
                </button>
              )}
              {filtered.map((item, idx) => {
                const itemId = getId(item)
                const isSelected = value.includes(itemId)
                const isFocused = focusedIndex === idx
                return (
                  <button
                    key={itemId}
                    id={optionRowId(listboxId, idx)}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => toggle(itemId)}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-md',
                      isSelected ? selectedClassName : 'hover:bg-fill-10',
                      isFocused && 'outline-none ring-2 ring-stroke-selected',
                    )}
                  >
                    {/* Checkbox indicator */}
                    <span
                      aria-hidden="true"
                      className={cn(
                        'flex items-center justify-center w-4 h-4 rounded border flex-shrink-0',
                        isSelected
                          ? 'bg-action-primary border-action-primary'
                          : 'border-stroke-light bg-surface-elevated',
                      )}
                    >
                      {isSelected && (
                        <Check className="w-3 h-3 text-white-static" />
                      )}
                    </span>
                    {renderOption(item)}
                  </button>
                )
              })}
              {filtered.length === 0 && (
                <div className="px-3 py-4 text-center text-sm text-text-secondary">
                  {emptyLabel}
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}
